import * as THREE from "three";

/**
 * Sentry Turret — the Ranger's signature ability. A short-lived automated
 * emplacement that tracks the nearest enemy and looses bolts through the normal
 * combat projectile pipeline (so its shots damage foes and smash props). Fully
 * procedural + self-disposing; the Game owns a list of live turrets.
 */
export class Turret {
  constructor(scene, x, z, groundH = 0, opts = {}) {
    this.scene = scene;
    this.x = x; this.z = z; this.groundH = groundH;
    this.life = opts.life ?? 8;
    this.range = opts.range ?? 11;
    this.fireRate = opts.fireRate ?? 0.7;
    this.dmg = opts.dmg ?? 10;
    this.fireCd = 0.25;   // small warm-up
    this.yaw = 0;
    this.dead = false;
    this._build();
  }

  _build() {
    const g = new THREE.Group();
    g.position.set(this.x, this.groundH, this.z);
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.42, 0.28, 10),
      new THREE.MeshStandardMaterial({ color: 0x445536, roughness: 0.85 }));
    base.position.y = 0.14; g.add(base);
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.12, 0.42, 8),
      new THREE.MeshStandardMaterial({ color: 0x5a4a30, roughness: 0.8 }));
    post.position.y = 0.5; g.add(post);
    this.head = new THREE.Group(); this.head.position.y = 0.78; g.add(this.head);
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.28, 0.44),
      new THREE.MeshStandardMaterial({ color: 0x7bd645, emissive: 0x2f6a1a, emissiveIntensity: 0.45, metalness: 0.3, roughness: 0.5 }));
    this.head.add(body);
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 0.5, 8),
      new THREE.MeshStandardMaterial({ color: 0x33421f, roughness: 0.6 }));
    barrel.rotation.x = Math.PI / 2; barrel.position.z = 0.36; this.head.add(barrel);
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.06, 8, 8), new THREE.MeshBasicMaterial({ color: 0xcaff8a }));
    eye.position.set(0, 0.07, 0.2); this.head.add(eye);
    g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
    this.mesh = g;
    this.scene.add(g);
  }

  _nearest(combat) {
    let best = null, bd = this.range;
    for (const e of combat.enemies) {
      if (e.dead) continue;
      const d = Math.hypot(e.pos.x - this.x, e.pos.z - this.z);
      if (d < bd) { bd = d; best = e; }
    }
    return best;
  }

  update(dt, combat) {
    if (this.dead) return;
    this.life -= dt;
    if (this.life <= 0) { this.dispose(); return; }
    this.fireCd -= dt;
    const target = this._nearest(combat);
    if (target) {
      const yaw = Math.atan2(target.pos.x - this.x, target.pos.z - this.z);
      // shortest-arc turn toward the target
      this.yaw += (((yaw - this.yaw + Math.PI * 3) % (Math.PI * 2)) - Math.PI) * Math.min(1, dt * 10);
      this.head.rotation.y = this.yaw;
      if (this.fireCd <= 0) {
        this.fireCd = this.fireRate;
        combat.spawnBolt(this.x, this.z, this.yaw, { dmg: this.dmg, color: "#caff8a", range: this.range + 2, speed: 24 });
      }
    }
    if (this.life < 1) this.mesh.scale.setScalar(Math.max(0.2, this.life));   // shrink out at the end
  }

  dispose() {
    if (this.dead && !this.mesh) return;
    this.dead = true;
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh.traverse((n) => {
        if (n.geometry) n.geometry.dispose();
        if (n.material) (Array.isArray(n.material) ? n.material : [n.material]).forEach((m) => m.dispose());
      });
      this.mesh = null;
    }
  }
}
